import CentredLoader from "../CentredLoader";
// Drawing utils now handled by useDrawingOperations hook
import NavigationBar from "./components/NavigationBar";
import PdfViewerDialogFooter from "./components/PdfViewerDialogFooter";
import PdfViewerModals from "./components/PdfViewerModals";
import PdfViewerPanel from "./components/PdfViewerPanel";
import ResizeHandle from "./components/ResizeHandle";
import TablePanel from "./components/TablePanel";
import {
  USE_SERVER_SIDE_GRIDLINES,
  COLUMN_ADDITION_MULTI_PAGE,
  COLUMN_MOVEMENT_MULTI_PAGE,
} from "./config/gridLinesConfig";
import useCellOperations from "./hooks/useCellOperations";
import useDrawingOperations from "./hooks/useDrawingOperations";
import useGridLineConfirmation from "./hooks/useGridLineConfirmation";
import useGridLineOperations from "./hooks/useGridLineOperations";
import useGridLinePlacement from "./hooks/useGridLinePlacement";
import useModalHandlers from "./hooks/useModalHandlers";
import usePdfRendering from "./hooks/usePdfRendering";
import usePdfTableInitialization from "./hooks/usePdfTableInitialization";
// Refactored modules
import usePdfTableState from "./hooks/usePdfTableState";
import useScrollSync from "./hooks/useScrollSync";
import useTableIdSystem from "./hooks/useTableIdSystem";
import useTableOperations from "./hooks/useTableOperations";
import {
  useTableChangeHandler,
  useKeyDownHandler,
  useTableAnalysisSubmissionHandler,
} from "./utils/eventHandlers";
import { convertCanvasToPdfCoordinates } from "./utils/geometryCalculations";
// PDF rendering now handled by usePdfRendering hook
import { recalculateCellsFromGridLines } from "./utils/gridLineRecalculation";
import { isMultiPageTable, getTablePages } from "./utils/multiPageTableUtils";
import {
  getCanvasCoordinates,
  findCellAtPdfCoordinates,
} from "./utils/pdfTableViewerUtils";
import { useResizeHandlers } from "./utils/resizeUtils";
import {
  calculateColumnStats,
  getCellDifferences,
} from "./utils/tableCalculations";
import { extractTablesFromResult } from "./utils/tableExtractionUtils";
import {
  renderNewColumnPreview,
  renderTablePreview,
} from "./utils/tablePreviewUtils";
import { PictureAsPdf } from "@mui/icons-material";
import {
  Box,
  Dialog,
  DialogTitle,
  DialogContent,
  useTheme,
} from "@mui/material";
import * as logger from "common/logger";
import React, { useCallback, useMemo, memo } from "react";
import { useGenerateExcelFromTablesMutation } from "services/aiAgents";
import { useFileBlobQuery } from "services/filesService";

const PdfTableViewer = ({
  fileId,
  fileDownloadUrl,
  extractionResult,
  onApprove,
  onCancel,
}) => {
  const theme = useTheme();
  // Stable sx objects to prevent re-renders
  const dialogContentStyles = useMemo(
    () => ({
      p: 0,
      display: "flex",
      flexDirection: "column",
      flex: 1,
      position: "relative",
    }),
    []
  );

  const mainContentStyles = useMemo(
    () => ({
      display: "flex",
      flex: 1,
      pt: 8,
      position: "relative",
    }),
    []
  );

  // Extract all state management to organized hook
  const {
    pdfState,
    tableState,
    uiState,
    modalState,
    gridLineState,
    drawingState,
    editingState,
    idSystemState,
    refs,
  } = usePdfTableState();

  // Initialize table ID system hook
  const {
    initializeTableIds: initializeTableIdsInternal,
    columnIds,
    rowIds,
    deletedColumns,
    deletedRows,
    originalTableStructure,
  } = useTableIdSystem(idSystemState, tableState.tableData);

  // Initialize table operations hook
  const {
    removeRow,
    removeColumnGlobally,
    recoverRowById,
    recoverColumnById,
    recoverAllRows,
    recoverAllColumns,
  } = useTableOperations(tableState, {
    ...idSystemState,
    columnIds,
    rowIds,
    deletedColumns,
    deletedRows,
    originalTableStructure,
  });

  // Initialize PDF rendering hook
  const {
    loadPdf,
    renderPage,
    highlightWords,
    clearPdfHighlights,
    // Pre-rendering for instant page flips
    preRenderAllPages,
  } = usePdfRendering(
    pdfState,
    tableState,
    refs,
    uiState.setIsInitialPdfRenderComplete
  );

  // Force render on page changes for immediate feedback (must be after renderPage)
  const renderPageForced = useCallback(
    async (pageNumber = null, selectedCell = null) => {
      await renderPage(pageNumber, selectedCell, true); // Force immediate render
    },
    [renderPage]
  );

  // Initialize cell operations hook
  const {
    handleCellClick,
    handleWordClick,
    startCellEdit,
    saveCellEdit,
    cancelCellEdit,
    handlePdfClick,
  } = useCellOperations(
    tableState,
    pdfState,
    editingState,
    highlightWords,
    refs,
    getCanvasCoordinates,
    convertCanvasToPdfCoordinates,
    findCellAtPdfCoordinates
  );

  // Initialize scroll synchronization hook
  const { handleTableScroll } = useScrollSync(
    refs.beforeTableRef,
    refs.afterTableRef
  );

  // Initialize modal handlers hook
  const { handleCellDetailClick } = useModalHandlers(
    gridLineState,
    modalState,
    tableState,
    pdfState
  );

  // Grid line configuration flags
  const limitColumnAdditionsToCurrentPage = !COLUMN_ADDITION_MULTI_PAGE;
  const limitColumnMovementsToCurrentPage = !COLUMN_MOVEMENT_MULTI_PAGE;

  // Initialize drawing operations hook
  const {
    startDrawing,
    continueDrawing,
    finishDrawing,
    finishPolygon,
    cancelDrawing,
    confirmDrawing,
  } = useDrawingOperations(
    drawingState,
    modalState,
    tableState,
    pdfState,
    refs,
    getCanvasCoordinates,
    convertCanvasToPdfCoordinates
  );

  // Destructure commonly used state for cleaner code
  const {
    currentPage,
    pdfScale,
    pdfDoc,
    totalPages,
    highlights,
    selectedWordIndex,
  } = pdfState;
  const {
    selectedTable,
    tableData,
    selectedCell,
    cachedTableData,
    setCachedTableData,
  } = tableState;
  const {
    pdfPanelExpanded,
    sidebarWidth,
    isResizing,
    showStats,
    showGrid,
    isProcessingContinue,
    isInitialPdfRenderComplete,
  } = uiState;
  const {
    dynamicGridLines,
    gridLineDragMode,
    isDraggingGridLine,
    draggingGridLine,
    pendingGridLineChanges,
    originalGridLines,
    isPlacingGridLine,
    placingGridLineType,
    isGridLineInPreview,
    showGridLineMoved,
    pendingNewGridLines,
  } = gridLineState;
  const { editingCell, editValue } = editingState;
  const {
    showGridLineConfirmation,
    showCellDetail,
    selectedCellDetail,
    showRecoveryMenu,
    recoveryMenuAnchor,
    cellInfoAnchor,
    showDrawingConfirmation,
  } = modalState;
  const {
    canvasRef,
    highlightContainerRef,
    pdfPageDimensions,
    containerRef,
    beforeTableRef,
    afterTableRef,
  } = refs;

  // Drawing state from hook
  const {
    drawingMode,
    drawingShape,
    previewWords,
    pendingShapeWords,
    polygonPoints,
  } = drawingState;

  // Resize handlers hook
  const { startResize } = useResizeHandlers(
    isResizing,
    uiState.setIsResizing,
    uiState.setSidebarWidth,
    containerRef
  );

  // Fetch PDF blob for display
  const {
    data: pdfBlob,
    isLoading: isPdfLoading,
    isError: isPdfError,
  } = useFileBlobQuery(fileId, {
    skip: !fileId,
  });

  const [generateExcelFromTables] = useGenerateExcelFromTablesMutation();

  // Extract tables from result - include both combined and individual tables
  const tables = useMemo(() => extractTablesFromResult(extractionResult), [
    extractionResult,
  ]);

  // Initialize PDF and table loading/rendering logic
  usePdfTableInitialization({
    // PDF state
    pdfBlob,
    pdfDoc,
    totalPages,
    currentPage,
    pdfPanelExpanded,
    selectedCell,

    // UI State
    isInitialPdfRenderComplete,

    // Table state
    tables,
    selectedTable,
    tableData,

    // Drawing state
    drawingShape,
    showDrawingConfirmation,
    pdfPageDimensions,

    // Grid line state
    isDraggingGridLine,
    isGridLineInPreview,
    limitColumnAdditionsToCurrentPage,

    // State setters
    pdfState,
    tableState,
    drawingState,
    gridLineState,

    // Functions
    loadPdf,
    renderPage: renderPageForced, // Use forced render for page changes
    initializeTableIdsInternal,
    highlightWords,
    preRenderAllPages,
  });

  // Wrapper for the utility function
  const recalculateCellsFromGridLinesWrapper = useCallback(
    (passedOriginalGridLines = null, passedNewGridLines = null) => {
      return recalculateCellsFromGridLines(
        dynamicGridLines,
        currentPage,
        selectedTable,
        tableData,
        originalGridLines,
        pdfPageDimensions,
        passedOriginalGridLines,
        passedNewGridLines,
        limitColumnAdditionsToCurrentPage,
        limitColumnMovementsToCurrentPage,
        USE_SERVER_SIDE_GRIDLINES
      );
    },
    [
      dynamicGridLines,
      currentPage,
      selectedTable,
      tableData,
      originalGridLines,
      pdfPageDimensions,
      limitColumnAdditionsToCurrentPage,
      limitColumnMovementsToCurrentPage,
    ]
  );

  // Initialize grid line operations hook (after wrapper is defined)
  const {
    startGridLineDrag: startGridLineDragHook,
    continueGridLineDrag: continueGridLineDragHook,
    cancelGridLinePlacement: cancelGridLinePlacementHook,
  } = useGridLineOperations(
    pdfState,
    gridLineState,
    uiState,
    drawingState,
    refs,
    tableState,
    recalculateCellsFromGridLinesWrapper
  );

  // Initialize grid line placement hook (after wrapper is defined)
  const {
    handleGridLinePlacement,
    startAddNewRow,
    startAddNewColumn,
  } = useGridLinePlacement(
    pdfState,
    tableState,
    gridLineState,
    drawingState,
    refs,
    recalculateCellsFromGridLinesWrapper
  );

  // Initialize grid line confirmation hook
  const {
    cancelGridLineDrag,
    confirmGridLineChanges,
    cancelGridLineChanges,
    stopGridLineDrag,
    cancelGridLinePreview,
    confirmGridLineMoved,
    cancelGridLineMoved,
  } = useGridLineConfirmation({
    // State
    gridLineState,
    modalState,
    tableState,

    // Grid line state
    isDraggingGridLine,
    isGridLineInPreview,
    pendingGridLineChanges,
    originalGridLines,
    dynamicGridLines,
    placingGridLineType,
    draggingGridLine,

    // Table state
    selectedTable,
    tableData,
    currentPage,
    pdfPageDimensions,
    showGridLineConfirmation,

    // Functions
    recalculateCellsFromGridLinesWrapper,
    continueGridLineDragHook,
  });

  // Event handlers hooks (after all dependencies are defined) - memoized
  const handleTableChange = useTableChangeHandler({
    tables,
    selectedTable,
    tableData,
    setSelectedTable: tableState.setSelectedTable,
    setSelectedCell: tableState.setSelectedCell,
    setEditingCell: editingState.setEditingCell,
    setEditValue: editingState.setEditValue,
    clearHighlights: clearPdfHighlights,
    setDeletedColumns: idSystemState.setDeletedColumns,
    setDeletedRows: idSystemState.setDeletedRows,
    setCurrentPage: pdfState.setCurrentPage,
    setTableData: tableState.setTableData,
    initializeTableIdsInternal,
    setColumnIds: idSystemState.setColumnIds,
    setRowIds: idSystemState.setRowIds,
    setOriginalTableStructure: idSystemState.setOriginalTableStructure,
    cachedTableData,
    setCachedTableData,
    // Grid line state setters
    setDynamicGridLines: gridLineState.setDynamicGridLines,
    setOriginalGridLines: gridLineState.setOriginalGridLines,
    setPendingGridLineChanges: gridLineState.setPendingGridLineChanges,
    setIsDraggingGridLine: gridLineState.setIsDraggingGridLine,
    setDraggingGridLine: gridLineState.setDraggingGridLine,
    setIsGridLineInPreview: gridLineState.setIsGridLineInPreview,
    setPendingNewGridLines: gridLineState.setPendingNewGridLines,
    setIsPlacingGridLine: gridLineState.setIsPlacingGridLine,
  });

  useKeyDownHandler({
    isPlacingGridLine,
    isDraggingGridLine,
    isGridLineInPreview,
    cancelGridLinePlacement: cancelGridLinePlacementHook,
    cancelGridLineDrag,
    cancelGridLinePreview,
  });

  const handleSubmitTableForAnalysis = useTableAnalysisSubmissionHandler({
    setIsProcessingContinue: uiState.setIsProcessingContinue,
    tableData,
    selectedTable,
    confidenceThreshold: uiState.confidenceThreshold,
    confidenceFilterEnabled: uiState.confidenceFilterEnabled,
    onApprove,
    fileId,
    fileDownloadUrl,
    extractionResult,
  });

  // Handle Excel download for multiple tables
  const handleDownloadExcel = async (selectedTableIds) => {
    try {
      // Gather data for all selected tables
      const tablesToExport = selectedTableIds
        .map((tableId) => {
          // Check if we have cached (edited) data for this table
          if (cachedTableData && cachedTableData[tableId]) {
            // Find the table metadata to get the name/group
            const tableMeta = tables.find(
              (t) =>
                !t.isSeparator && (t.table_id === tableId || t.id === tableId)
            );
            return {
              ...cachedTableData[tableId],
              group_name: tableMeta?.group_name,
              table_name: `Table ${tables.indexOf(tableMeta)}`,
              id: tableId,
            };
          }

          // If current table is selected and has unsaved edits in tableData
          if (
            selectedTable &&
            (selectedTable.table_id === tableId || selectedTable.id === tableId)
          ) {
            return {
              ...tableData,
              group_name: selectedTable.group_name,
              table_name: `Table ${tables.indexOf(selectedTable)}`,
              id: tableId,
            };
          }

          // Otherwise fall back to original extraction result data
          const table = tables.find(
            (t) =>
              !t.isSeparator && (t.table_id === tableId || t.id === tableId)
          );

          if (table) {
            return {
              headers: table.headers || [],
              rows: table.rows || [],
              group_name: table.group_name,
              table_name: `Table ${tables.indexOf(table)}`,
              id: tableId,
            };
          }
          return null;
        })
        .filter(Boolean);

      if (tablesToExport.length === 0) {
        throw new Error("No valid tables selected for export");
      }

      await generateExcelFromTables({
        tables: tablesToExport,
        originalFileS3Key: fileId,
        addMetaPage: true,
      });
    } catch (error) {
      logger.exception(error);
    }
  };

  // Memoize expensive functions passed as props
  const isMultiPageTableMemo = useCallback(
    () => isMultiPageTable(selectedTable),
    [selectedTable]
  );
  const getTablePagesMemo = useCallback(() => getTablePages(selectedTable), [
    selectedTable,
  ]);
  const calculateColumnStatsMemo = useCallback(calculateColumnStats, []);
  const getCellDifferencesMemo = useCallback(getCellDifferences, []);
  const renderNewColumnPreviewMemo = useCallback(
    (...args) => renderNewColumnPreview(...args, theme.brand.gray.gainsboro),
    [theme]
  );
  const renderTablePreviewMemo = useCallback(
    (...args) => renderTablePreview(...args, theme.brand.gray.gainsboro),
    [theme]
  );
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const handleTableScrollMemo = useCallback(handleTableScroll, []);

  // Global escape key handler for placement mode and preview mode
  return (
    <Dialog
      open={true}
      onClose={onCancel}
      maxWidth={false}
      fullWidth
      PaperProps={{
        sx: {
          width: "95vw",
          height: { xs: "95vh", sm: "90vh" },
          maxWidth: "none",
          maxHeight: "none",
          m: { xs: 0.5, sm: 1 },
        },
      }}
    >
      <DialogTitle
        sx={{ display: "flex", alignItems: "center", gap: 1, pb: 1 }}
      >
        <PictureAsPdf color={"primary"} />
        {"PDF Extraction Viewer"}
      </DialogTitle>

      <DialogContent sx={dialogContentStyles}>
        {/* Loading overlay - covers everything */}
        {isProcessingContinue ? (
          <CentredLoader
            label={"Processing..."}
            showProgress={true}
            useEnhancedAnimation={true}
          />
        ) : (
          <>
            {/* Floating Navigation Bar */}
            <NavigationBar
              // Table Selector
              tables={tables}
              selectedTable={selectedTable}
              handleTableChange={handleTableChange}
              // Toggles
              showStats={showStats}
              setShowStats={uiState.setShowStats}
              // PDF Panel Toggle
              pdfPanelExpanded={pdfPanelExpanded}
              setPdfPanelExpanded={uiState.setPdfPanelExpanded}
            />

            {/* Main Content - Resizable Layout */}
            <Box ref={containerRef} sx={mainContentStyles}>
              {/* Left Panel - Table */}
              <TablePanel
                pdfPanelExpanded={pdfPanelExpanded}
                sidebarWidth={sidebarWidth}
                selectedTable={selectedTable}
                tableData={tableData}
                currentPage={currentPage}
                setCurrentPage={pdfState.setCurrentPage}
                showStats={showStats}
                setShowStats={uiState.setShowStats}
                showConfidence={uiState.showConfidence}
                setShowConfidence={uiState.setShowConfidence}
                tableZoom={uiState.tableZoom}
                setTableZoom={uiState.setTableZoom}
                confidenceFilterEnabled={uiState.confidenceFilterEnabled}
                setConfidenceFilterEnabled={uiState.setConfidenceFilterEnabled}
                confidenceThreshold={uiState.confidenceThreshold}
                setConfidenceThreshold={uiState.setConfidenceThreshold}
                rowIds={rowIds}
                deletedRows={deletedRows}
                deletedColumns={deletedColumns}
                recoverRowById={recoverRowById}
                recoverColumnById={recoverColumnById}
                recoverAllRows={recoverAllRows}
                recoverAllColumns={recoverAllColumns}
                editingCell={editingCell}
                selectedCell={selectedCell}
                editValue={editValue}
                setEditValue={editingState.setEditValue}
                handleCellClick={handleCellClick}
                startCellEdit={startCellEdit}
                saveCellEdit={saveCellEdit}
                cancelCellEdit={cancelCellEdit}
                setCellInfoAnchor={modalState.setCellInfoAnchor}
                removeColumnGlobally={removeColumnGlobally}
                removeRow={removeRow}
                isMultiPageTable={isMultiPageTableMemo}
                getTablePages={getTablePagesMemo}
                calculateColumnStats={calculateColumnStatsMemo}
              />

              {/* Resize Handle */}
              {pdfPanelExpanded && (
                <ResizeHandle
                  isResizing={isResizing}
                  onMouseDown={startResize}
                />
              )}

              {/* Right Panel - PDF (Resizable Sidebar) */}
              <PdfViewerPanel
                // Panel state
                pdfPanelExpanded={pdfPanelExpanded}
                sidebarWidth={sidebarWidth}
                // PDF state
                isPdfLoading={isPdfLoading}
                isPdfError={isPdfError}
                pdfScale={pdfScale}
                setPdfScale={pdfState.setPdfScale}
                currentPage={currentPage}
                setCurrentPage={pdfState.setCurrentPage}
                totalPages={totalPages}
                pdfDoc={pdfDoc}
                // Refs
                highlightContainerRef={highlightContainerRef}
                canvasRef={canvasRef}
                pdfPageDimensions={pdfPageDimensions}
                // Table data
                selectedTable={selectedTable}
                tableData={tableData}
                showGrid={showGrid}
                setShowGrid={uiState.setShowGrid}
                dynamicGridLines={dynamicGridLines}
                deletedRows={deletedRows}
                deletedColumns={deletedColumns}
                // Highlighting
                highlights={highlights}
                // Drawing state
                drawingMode={drawingMode}
                setDrawingMode={drawingState.setDrawingMode}
                isPlacingGridLine={isPlacingGridLine}
                isDraggingGridLine={isDraggingGridLine}
                placingGridLineType={placingGridLineType}
                draggingGridLine={draggingGridLine}
                gridLineDragMode={gridLineDragMode}
                pendingShapeWords={pendingShapeWords}
                pendingNewGridLines={pendingNewGridLines}
                drawingShape={drawingShape}
                polygonPoints={polygonPoints}
                previewWords={previewWords}
                showDrawingConfirmation={showDrawingConfirmation}
                showGridLineConfirmation={showGridLineConfirmation}
                setShowGridLineConfirmation={
                  modalState.setShowGridLineConfirmation
                }
                // Table Structure Controls
                startAddNewRow={startAddNewRow}
                startAddNewColumn={startAddNewColumn}
                cancelGridLinePlacement={cancelGridLinePlacementHook}
                // Recovery Controls
                setRecoveryMenuAnchor={modalState.setRecoveryMenuAnchor}
                setShowRecoveryMenu={modalState.setShowRecoveryMenu}
                // Event handlers
                startDrawing={startDrawing}
                continueDrawing={continueDrawing}
                finishDrawing={finishDrawing}
                cancelDrawing={cancelDrawing}
                finishPolygon={finishPolygon}
                handleGridLinePlacement={handleGridLinePlacement}
                continueGridLineDrag={continueGridLineDragHook}
                startGridLineDrag={startGridLineDragHook}
                stopGridLineDrag={stopGridLineDrag}
                handlePdfClick={handlePdfClick}
                showGridLineMoved={showGridLineMoved}
                confirmGridLineMoved={confirmGridLineMoved}
                cancelGridLineMoved={cancelGridLineMoved}
              />
            </Box>

            {/* All Modal Dialogs */}
            <PdfViewerModals
              cellInfoAnchor={cellInfoAnchor}
              showDrawingConfirmation={showDrawingConfirmation}
              showGridLineConfirmation={showGridLineConfirmation}
              showCellDetail={showCellDetail}
              showRecoveryMenu={showRecoveryMenu}
              recoveryMenuAnchor={recoveryMenuAnchor}
              selectedCell={selectedCell}
              selectedWordIndex={selectedWordIndex}
              pendingShapeWords={pendingShapeWords}
              selectedTable={selectedTable}
              pendingGridLineChanges={pendingGridLineChanges}
              currentPage={currentPage}
              selectedCellDetail={selectedCellDetail}
              deletedRows={deletedRows}
              deletedColumns={deletedColumns}
              beforeTableRef={beforeTableRef}
              afterTableRef={afterTableRef}
              pdfPageDimensions={pdfPageDimensions}
              confidenceFilterEnabled={uiState.confidenceFilterEnabled}
              confidenceThreshold={uiState.confidenceThreshold}
              modalState={modalState}
              handleWordClick={handleWordClick}
              confirmDrawing={confirmDrawing}
              isMultiPageTable={isMultiPageTableMemo}
              getTablePages={getTablePagesMemo}
              getCellDifferences={getCellDifferencesMemo}
              renderNewColumnPreview={renderNewColumnPreviewMemo}
              renderTablePreview={renderTablePreviewMemo}
              handleTableScroll={handleTableScrollMemo}
              confirmGridLineChanges={confirmGridLineChanges}
              cancelGridLineChanges={cancelGridLineChanges}
              handleCellDetailClick={handleCellDetailClick}
              recoverRowById={recoverRowById}
              recoverColumnById={recoverColumnById}
              recoverAllRows={recoverAllRows}
              recoverAllColumns={recoverAllColumns}
            />
          </>
        )}
      </DialogContent>

      <PdfViewerDialogFooter
        tables={tables}
        selectedTable={selectedTable}
        onCancel={onCancel}
        onContinue={handleSubmitTableForAnalysis}
        onDownloadExcel={handleDownloadExcel}
      />
    </Dialog>
  );
};

export default memo(PdfTableViewer);
